import { useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Confetti } from "@/components/tools/Confetti";
import {
  CopyButton,
  BrandedExportActions,
  EmptyHint,
  FullscreenButton,
  HistoryPanel,
  ListInput,
  Panel,
  ResetButton,
  SoundToggle,
  usePersistentState,
  useSfx,
} from "@/components/tools/ToolKit";
import { DEFAULT_CLASS_LIST, dedupe, parseList, pickOne, shuffle } from "@/lib/random-engine";
import { UserCheck, Shuffle, ArrowUpDown, RotateCcw, Trophy, Trash2, Copy, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { copyText } from "@/lib/random-engine";

type Phase = "IDLE" | "RUNNING" | "RESULT";

export function NamePicker({
  storageKey,
  label = "Student Names",
  roles,
}: {
  storageKey: string;
  label?: string;
  roles?: string[];
}) {
  const [raw, setRaw] = usePersistentState(storageKey, DEFAULT_CLASS_LIST.join("\n"));
  const [soundOn, setSoundOn] = usePersistentState(`${storageKey}:sound`, true);
  const [removeWinner, setRemoveWinner] = useState(false);
  const [pickCount, setPickCount] = useState(1);
  const [phase, setPhase] = useState<Phase>("IDLE");
  const [display, setDisplay] = useState<string>("Ready when you are");
  const [winner, setWinner] = useState<string | null>(null);
  const [winnersList, setWinnersList] = useState<string[]>([]);
  const [showWinnerModal, setShowWinnerModal] = useState(false);
  const [history, setHistory] = useState<string[]>([]);
  const [role, setRole] = useState<string>(roles?.[0] ?? "");
  const [confetti, setConfetti] = useState(false);

  const stageRef = useRef<HTMLDivElement>(null);
  const sfx = useSfx(soundOn);

  const names = dedupe(parseList(raw));
  const canStart = names.length >= pickCount && phase !== "RUNNING";

  const start = () => {
    if (!canStart) return;

    // Pick 1 or multiple names
    const chosenList: string[] = [];
    const pool = [...names];
    for (let i = 0; i < pickCount; i++) {
      if (pool.length === 0) break;
      const picked = pickOne(pool);
      if (picked) {
        chosenList.push(picked);
        const idx = pool.indexOf(picked);
        if (idx !== -1) pool.splice(idx, 1);
      }
    }

    if (chosenList.length === 0) return;

    setPhase("RUNNING");
    setWinner(null);
    setWinnersList([]);
    setShowWinnerModal(false);
    setConfetti(false);
    sfx.whoosh();

    const total = 1400;
    const startedAt = performance.now();

    const tick = () => {
      const elapsed = performance.now() - startedAt;
      if (elapsed >= total) {
        const resultText = chosenList.join(", ");
        setDisplay(resultText);
        setWinner(resultText);
        setWinnersList(chosenList);
        setPhase("RESULT");
        setShowWinnerModal(true);

        const logEntry = role ? `${resultText} — ${role}` : resultText;
        setHistory((h) => [logEntry, ...h].slice(0, 50));
        setConfetti(true);
        sfx.fanfare();

        window.setTimeout(() => setConfetti(false), 2200);

        if (removeWinner) {
          setRaw(names.filter((n) => !chosenList.includes(n)).join("\n"));
        }
        return;
      }

      setDisplay(pickOne(names) ?? "");
      sfx.tick(Math.max(0.2, 1 - elapsed / total));
      const delay = 40 + (elapsed / total) * 160;
      window.setTimeout(tick, delay);
    };

    tick();
  };

  const removeCurrentWinners = () => {
    if (winnersList.length === 0) return;
    setRaw(names.filter((n) => !winnersList.includes(n)).join("\n"));
    toast.success(`Removed ${winnersList.length > 1 ? `${winnersList.length} names` : `"${winnersList[0]}"`}`);
    setShowWinnerModal(false);
  };

  const shuffleNames = () => {
    sfx.click();
    setRaw(shuffle(names).join("\n"));
  };

  const sortNames = () => {
    sfx.click();
    setRaw([...names].sort().join("\n"));
  };

  const reset = () => {
    setPhase("IDLE");
    setWinner(null);
    setWinnersList([]);
    setDisplay("Ready when you are");
    setHistory([]);
  };

  return (
    <>
      <div className="grid min-w-0 gap-6 lg:grid-cols-[340px_1fr_320px] xl:grid-cols-[360px_1fr_340px]">
        {/* Settings Panel */}
        <Panel title="Roster & Setup" className="no-print min-w-0 space-y-4">
          <div>
            <ListInput value={raw} onChange={setRaw} label={label} count={names.length} />
            <div className="mt-2 flex gap-1.5">
              <Button type="button" variant="outline" size="sm" onClick={shuffleNames} disabled={names.length < 2}>
                <Shuffle className="size-3.5 mr-1" /> Shuffle
              </Button>
              <Button type="button" variant="outline" size="sm" onClick={sortNames} disabled={names.length < 2}>
                <ArrowUpDown className="size-3.5 mr-1" /> Sort A-Z
              </Button>
            </div>
          </div>

          {roles && (
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground block mb-1">
                Classroom Job / Role
              </label>
              <select
                value={role}
                onChange={(e) => setRole(e.target.value)}
                className="h-10 w-full rounded-xl border border-input bg-card px-3 text-sm font-medium"
              >
                {roles.map((r) => (
                  <option key={r} value={r}>
                    {r}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div>
            <p className="mb-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              How many to pick
            </p>
            <div className="flex flex-wrap gap-1.5">
              {[1, 2, 3, 5].map((n) => (
                <Button
                  key={n}
                  type="button"
                  size="sm"
                  variant={pickCount === n ? "default" : "outline"}
                  onClick={() => setPickCount(n)}
                >
                  {n} {n === 1 ? "Name" : "Names"}
                </Button>
              ))}
            </div>
          </div>

          <div className="flex items-center justify-between rounded-xl border bg-secondary/50 px-3.5 py-2.5">
            <span className="text-xs font-medium">Remove winner from list</span>
            <Switch checked={removeWinner} onCheckedChange={setRemoveWinner} />
          </div>

          <div className="flex flex-wrap gap-2 pt-1">
            <SoundToggle on={soundOn} onToggle={() => setSoundOn(!soundOn)} />
            <ResetButton onClick={reset} />
          </div>
        </Panel>

        {/* Main Stage */}
        <div
          ref={stageRef}
          className="surface-card relative min-w-0 flex flex-col items-center justify-between gap-6 p-6 min-h-[460px] sm:min-h-[540px] lg:min-h-[600px] xl:min-h-[660px]"
        >
          <Confetti active={confetti} />
          <div className="w-full flex items-center justify-between no-print">
            <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
              <UserCheck className="size-4 text-primary" />
              <span>{names.length} {names.length === 1 ? "Name" : "Names"}</span>
            </div>
            <FullscreenButton targetRef={stageRef} />
          </div>

          {/* Name Display Card */}
          <div className="my-auto flex flex-col items-center justify-center text-center p-6 sm:p-8 w-full max-w-2xl">
            {role && (
              <span className="mb-3 inline-block rounded-full bg-secondary px-4 py-1.5 text-xs sm:text-sm font-bold uppercase tracking-wider text-muted-foreground">
                {role}
              </span>
            )}

            <p
              key={display}
              className={`font-display text-4xl sm:text-6xl lg:text-7xl font-extrabold tracking-tight break-words text-foreground ${
                phase === "RESULT"
                  ? "animate-reveal text-primary"
                  : phase === "RUNNING"
                    ? "animate-flick opacity-85"
                    : "opacity-60"
              }`}
            >
              {display}
            </p>
          </div>

          {/* Action Button */}
          <div className="flex flex-col items-center gap-3 w-full">
            {names.length < pickCount ? (
              <EmptyHint>Add at least {pickCount} names to start.</EmptyHint>
            ) : (
              <Button
                size="lg"
                className="h-14 px-12 text-lg font-bold"
                disabled={phase === "RUNNING"}
                onClick={() => {
                  sfx.click();
                  start();
                }}
              >
                <RotateCcw className={`size-5 mr-2 ${phase === "RUNNING" ? "animate-spin" : ""}`} />
                {phase === "RUNNING"
                  ? "Picking…"
                  : phase === "RESULT"
                    ? "Pick Again"
                    : pickCount > 1
                      ? `Pick ${pickCount} Names`
                      : "Pick a Name"}
              </Button>
            )}

            {winner && (
              <div className="flex flex-wrap justify-center gap-2">
                <CopyButton text={winner} label="Copy Winner" />
                <BrandedExportActions
                  title={winnersList.length > 1 ? "Selected Winners" : "Winner Selection"}
                  content={winner}
                  filename="picamini-winner-result.png"
                />
              </div>
            )}
          </div>
        </div>

        {/* History Panel */}
        <div className="min-w-0">
          <HistoryPanel items={history} onClear={() => setHistory([])} title="Selection History" />
        </div>
      </div>

      {/* 🎉 WINNER POPUP MODAL */}
      <Dialog open={showWinnerModal} onOpenChange={setShowWinnerModal}>
        <DialogContent className="w-[90vw] max-w-sm sm:max-w-md p-6 sm:p-7 rounded-2xl sm:rounded-3xl border border-border bg-card shadow-2xl text-center overflow-hidden">
          <DialogHeader className="flex flex-col items-center justify-center text-center space-y-2">
            <div className="flex size-14 items-center justify-center rounded-2xl bg-amber-500/10 text-amber-500 border border-amber-500/20">
              <Trophy className="size-7" />
            </div>
            <DialogTitle className="font-display text-2xl font-bold tracking-tight text-foreground">
              {winnersList.length > 1 ? "Selected Winners!" : "Name Picked!"}
            </DialogTitle>
          </DialogHeader>

          {/* Winner Display Box */}
          <div className="my-4 w-full rounded-2xl border border-primary/20 bg-primary/10 p-5 shadow-sm flex flex-col items-center">
            {role && (
              <span className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-1">
                {role}
              </span>
            )}
            <p className="font-display text-3xl font-extrabold text-primary break-words">
              {winner}
            </p>
          </div>

          {/* Actions Stack */}
          <div className="flex flex-col gap-2.5 w-full pt-1">
            <Button
              type="button"
              size="lg"
              className="w-full font-bold h-12 text-base"
              onClick={() => {
                setShowWinnerModal(false);
                start();
              }}
            >
              <RotateCcw className="size-4 mr-2" /> Pick Again
            </Button>

            <div className="grid grid-cols-2 gap-2 w-full">
              <Button
                type="button"
                variant="outline"
                size="default"
                className="w-full font-semibold text-xs sm:text-sm"
                onClick={async () => {
                  if (winner) {
                    await copyText(winner);
                    toast.success("Copied to clipboard");
                  }
                }}
              >
                <Copy className="size-3.5 mr-1.5" /> Copy
              </Button>

              <Button
                type="button"
                variant="destructive"
                size="default"
                className="w-full font-semibold text-xs sm:text-sm"
                onClick={removeCurrentWinners}
              >
                <Trash2 className="size-3.5 mr-1.5" /> Remove
              </Button>
            </div>
            {winner && (
              <BrandedExportActions
                title={winnersList.length > 1 ? "Selected Winners" : "Winner Selection"}
                content={winner}
                filename="picamini-winner-result.png"
              />
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
